import { useEffect, useRef, useState } from 'react';

/**
 * AnimatedNumber — cuenta desde `from` hasta `value` con easing atlético.
 * Respeta prefers-reduced-motion (renderiza el valor final directo).
 *
 * Props:
 *   value    — número objetivo (int o float)
 *   duration — ms (default 900)
 *   from     — número inicial (default 0)
 *   decimals — dígitos decimales (default: auto según value)
 *   prefix / suffix — strings opcionales
 *   className
 */
const AnimatedNumber = ({
  value = 0,
  duration = 900,
  from = 0,
  decimals,
  prefix = '',
  suffix = '',
  className = '',
}) => {
  const [display, setDisplay] = useState(from);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const startValueRef = useRef(from);

  const target = Number.isFinite(+value) ? +value : 0;
  const autoDecimals = decimals ?? (Number.isInteger(target) ? 0 : 1);

  useEffect(() => {
    // Respetar reduced motion
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || duration <= 0) {
      setDisplay(target);
      return;
    }

    startValueRef.current = display;
    startTimeRef.current = null;

    const step = (ts) => {
      if (!startTimeRef.current) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const progress = Math.min(1, elapsed / duration);
      // easeOutExpo (atlético, arranca rápido y frena elegante)
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const current = startValueRef.current + (target - startValueRef.current) * eased;
      setDisplay(current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  const formatted = Number.isFinite(display)
    ? display.toLocaleString('es-MX', {
        minimumFractionDigits: autoDecimals,
        maximumFractionDigits: autoDecimals,
      })
    : '—';

  return (
    <span className={`tabular-nums ${className}`}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default AnimatedNumber;
